/**
 * `settings`: what a fill was configured to do (RFC 0284 S6g).
 *
 * The last piece of the VS Code comparison. Apps could say what a package IS,
 * and nothing could say what it can be configured to DO, so a sidebar with a
 * row limit had to be REWRITTEN to change the limit. A module declares its
 * settings at install; a person changes them in Settings; the module reads them
 * back here and redraws.
 *
 * READ ONLY, unlike every other configuration surface you might compare this
 * to. A setting is the PERSON's, and a module that could write its own settings
 * could quietly undo a choice they made. If a fill wants state of its
 * own, that is a document, which is a capability apps already have and which is
 * disclosed at install.
 *
 * ALREADY MERGED, so a module never sees the difference between a declared
 * default and a value somebody set. Handing over both and a merge rule would
 * mean every fill reimplementing it, and getting it subtly different.
 */

import type { SettingValue } from '@mod/core/model'

export interface SettingsCapability {
  /**
   * One setting, or `undefined` for a key this app never declared.
   *
   * Undefined rather than a thrown error, because an app reading a key it
   * removed from its own spec is an ordinary state during an update, not a bug
   * worth taking a sidebar down for.
   */
  get(key: string): SettingValue | undefined
  /** Everything declared, with values applied. A copy. */
  all(): Record<string, SettingValue>
  /** Fires when a person changes one. Returns an unsubscribe. Without it a
   *  fill keeps whatever it read at mount, so changing a setting
   *  appears to do nothing until the pane is rebuilt. */
  onChange(fn: () => void): () => void
}

export interface SettingsDeps {
  values: () => Record<string, SettingValue>
  subscribe: (fn: () => void) => () => void
}

export function makeSettings(deps: SettingsDeps): SettingsCapability {
  return {
    get: (key) => deps.values()[key],
    all: () => ({ ...deps.values() }),
    onChange: (fn) => deps.subscribe(fn),
  }
}
